import { EventEmitter, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { DataBagService } from '../common/data-bag.service';
import { DateService } from '../common/date.service';
import { InfoboxService } from '../common/infobox/infobox.service';
import { LoaderService } from '../common/loader/loader.service';
import { AccommodationDataService } from './accommodation-data.service';

export interface MoveData {
	type: string;
	step: string;
	reservation: any;
	previous?: Array<any>;
}

@Injectable({
	providedIn: 'root',
})
export class AccommodationService {
	/*
	roomConfigurations:
	 - Single
	 - Double
	 - Extra - dostawka
	
	roomTypes
	 - Room
	 - Studio
	 - Apartament
*/
	changes = new EventEmitter();
	date;
	firstDay = '';
	destination = null;
	roomTypes: Array<any> = [];
	productsAll: Array<any> = [];
	products: Array<any> = [];
	productsSelected: Array<any> = [];
	accomodations: Array<any> = [];
	reservations: Array<any> = [];
	availabilities: Array<any> = [];
	nonames: Array<any> = [];
	nonamesRows = 0;
	checkerSize = {
		width: 0,
		height: 0,
	};
	private readonly oneDay = {
		width: 20,
		height: 40,
		offset: 10,
	};
	roomsProducts: Array<any> = [];
	move = new Subject<MoveData>();
	reservationQuartering;

	constructor(
		private readonly bag: DataBagService,
		private readonly data: AccommodationDataService,
		private readonly dateService: DateService,
		private readonly infobox: InfoboxService,
		private readonly loader: LoaderService,
	) {
		this.move.subscribe((data: MoveData) => {
			switch (data.type) {
				case 'dok':
					switch (data.step) {
						case 'begin':
							this.reservationQuartering = data.reservation;
							break;
						case 'selected':
							this.data.quartering(this.reservationQuartering, data.reservation, (result) => {
								if (!result) {
									return;
								}
								this.move.next({
									type: data.type,
									step: 'end',
									reservation: this.reservationQuartering,
								});
							});
							break;
						case 'end':
							this.reservationQuartering = null;
							break;
					}
					break;
			}
		});
	}

	loadAvailabilities() {
		this.loader.show('loadAvailabilities');
		this.products.forEach((product) => {
			product.availabilities = [];
		});
		const dateFrom = this.date;
		const dateTo = this.dateService.daysAdd(90, dateFrom);
		const parms = 'lower=' + dateFrom + '&upper=' + dateTo + '/?limit=999999';
		this.bag.get('/product/room/availability/' + parms, (result, error) => {
			this.parseAvailabilities(result.results, 'availabilities');
			this.loader.hide('loadAvailabilities');
		});
		this.bag.get('/product/room_slave/availability/' + parms, (result, error) => {
			this.parseAvailabilities(result.results, 'availabilities');
			this.loader.hide('loadAvailabilities');
		});
		this.loader.show('loadAvailabilitiesStates');
		this.bag.get('/product/room/availability_state/' + parms, (result, error) => {
			this.parseAvailabilities(result.results, 'availabilitiesStates');
			this.loader.hide('loadAvailabilitiesStates');
		});
	}

	parseReservations(result): void {
		result.data.forEach((reservation) => {
			reservation.roomTypeName = this.reservationRoomTypeName(reservation.roomType);
			if (reservation.room) {
				const room = this.roomsProducts.find((obj) => obj.room.id == reservation.room);
				if (!room) {
					const product = this.products.find((obj) => obj.id == reservation.productId);
					this.reservationToCartPut(reservation, product);
					return;
				}
				this.reservationToLodgePut(reservation, room.room);
				return;
			}
			if (reservation.noname != '0') {
				this.resevationToNonamePut(reservation);
				return;
			}
			const product = this.products.find((obj) => obj.id == reservation.productId);
			this.reservationToCartPut(reservation, product);
		});
	}

	parseAvailabilities(availabilities, type: string): void {
		availabilities.forEach((availability) => {
			const product = this.findProductByRoom(availability.room);
			if (!product) {
				return;
			}
			const coordinates = this.calculateCoordinates(
				availability.dateRange.lower,
				availability.dateRange.upper,
				true,
			);
			const room = availability.room ?? availability.roomSlave;
			const top = this.calculateCoordinatesForAccomodation(room, product);
			if (top === null) {
				// room not found
				return;
			}
			let background = '';
			let zindex = '';
			switch (type) {
				case 'availabilities':
					background = this.availabilityBackground(availability.availabilityType);
					zindex = '10';
					break;
				case 'availabilitiesStates':
					background = this.availabilityStateBackground(availability.state);
					zindex = '20';
					break;
				default:
					break;
			}
			availability.styles = {
				'top.px': top,
				'left.px': coordinates.left,
				'width.px': coordinates.width,
				'z-index': zindex,
				background: background,
			};
			product.availabilities.push(availability);
		});
	}

	private calculateCoordinatesForAccomodation(room, product): number {
		let position = product.rooms.findIndex((obj) => obj.id == room);
		if (position == -1) {
			position = product.rooms.roomSlaves.findIndex((obj) => obj.id == room);
			if (position == -1) {
				console.error('Room not found', room);
				return null;
			}
		}
		return this.oneDay.height * position;
	}

	private availabilityBackground(availabilityType: string): string {
		switch (availabilityType) {
			case 'A': // Allotment
				return 'rgba(0, 0, 255, 0.2)';
			case 'G': // Gwarancja
				return 'rgba(0, 255, 0, 0.2)';
		}
		return '';
	}

	private availabilityStateBackground(state: string): string {
		switch (state) {
			case 'L': // Zablokowany
				return 'rgba(255, 223, 211, 0.9)';
			case 'S': // Sprzedaż zablokowana
				return 'rgba(210, 145, 188, 0.9)';
			case 'R': // Zarezerwowany
				return 'rgba(177, 213, 232, 0.2)';
		}
		return '';
	}

	protected reservationStyle(type: string, reservation, data): boolean {
		const coordinates = this.calculateCoordinates(reservation.dateFrom, reservation.dateTo);
		if (coordinates.width < 0) {
			// out of screen
			return false;
		}
		const background: string = this.reservationBackground(reservation.noname, reservation.commitmentModeId);
		let top = 0;
		switch (type) {
			case 'unassigned':
				const colisions = this.howManyColisions(
					coordinates.left + this.oneDay.offset,
					coordinates.left + coordinates.width + this.oneDay.offset,
					data.otherObjects,
				);
				top = colisions * this.oneDay.height;
				reservation.row = colisions;
				break;
			case 'assigned':
				top = this.calculateCoordinatesForRoom(data.product, data.room);
				break;
		}
		if (coordinates.left == 0 && this.date != reservation.dateFrom) {
			// to looks pretty
			reservation.styles = {
				'top.px': top,
				'left.px': coordinates.left,
				'width.px': coordinates.width + this.oneDay.offset,
				'min-width.px': coordinates.width + this.oneDay.offset,
				background: background,
				'border-top-left-radius': 0,
				'border-bottom-left-radius': 0,
			};
			return true;
		}
		reservation.styles = {
			'top.px': top,
			'left.px': coordinates.left + this.oneDay.offset,
			'width.px': coordinates.width,
			'min-width.px': coordinates.width,
			background: background,
		};
		return true;
	}

	private howManyColisions(pixelFrom: number, pixelTo: number, otherObjects): number {
		let colisions = 0;
		let currentPixelFrom: number;
		let currentPixelTo: number;
		otherObjects.forEach((obj) => {
			if (obj.styles['left.px'] == undefined || obj.styles['width.px'] == undefined) {
				return;
			}
			currentPixelFrom = obj.styles['left.px'];
			currentPixelTo = parseInt(obj.styles['left.px']) + parseInt(obj.styles['width.px']);
			if (pixelTo > currentPixelFrom && pixelFrom < currentPixelTo) {
				if (obj.row > colisions) {
					colisions = obj.row;
				} else if (obj.row == colisions) {
					colisions++;
				}
			}
		});
		return colisions;
	}

	private calculateCoordinates(dateFrom: string, dateTo: string, forAccomodation = false) {
		let daysStart: number = this.dateService.daysBetween(this.date, dateFrom);
		let daysLong: number = this.dateService.daysBetween(dateFrom, dateTo);
		if (forAccomodation) {
			daysLong++;
		}
		if (daysStart < 0) {
			daysLong += daysStart;
			daysStart = 0;
		}
		const left: number = this.oneDay.width * daysStart;
		let width: number = this.oneDay.width * daysLong;
		if (left + width > this.checkerSize.width) {
			width = this.checkerSize.width - left - 15;
		}
		return { left, width };
	}

	private calculateCoordinatesForRoom(product, room): number {
		const position = product.rooms.findIndex((obj) => obj.id == room.id);
		if (position == -1) {
			const position = product.rooms.RoomSlaves.findIndex((obj) => obj.id == room.id);
			if (position == -1) {
				console.error('Room not found', room);
				return;
			}
		}
		return this.oneDay.height * position;
	}

	private findProductByRoom(roomId) {
		const room = this.roomsProducts.find((obj) => obj.room.id == roomId);
		if (!room) {
			return;
		}
		return room.product;
	}

	private reservationBackground(noname, commitmentModeId): string {
		switch (noname) {
			case '0':
				return commitmentModeId == '3' ? '#edf55f' : '#FFC300';
			case '1':
				return commitmentModeId == '3' ? '#BFC3FA' : '#838af2';
			case '2':
				return commitmentModeId == '3' ? '#17e317' : '#077d07';
		}
	}

	private reservationRoomTypeName(roomType) {
		const roomObject = this.roomTypes.find((obj) => obj.id == roomType);
		if (!roomObject) {
			return '';
		}
		return roomObject.code;
	}

	reservationToLodge(reservation, room) {
		this.removeReservationFrom(reservation);
		this.reservationToLodgePut(reservation, room);
	}

	private reservationToLodgePut(reservation, room) {
		const product = this.products.find((product) => product.rooms.find((findRoom) => findRoom.id == room.id));
		if (!product) {
			return;
		}
		if (
			!this.reservationStyle('assigned', reservation, {
				product: product,
				room: room,
			})
		) {
			return;
		}
		if (reservation.row > product.unassignedRows) {
			product.unassignedRows = reservation.row;
		}
		product.reservations.push(reservation);
	}

	reservationToCart(reservation) {
		this.removeReservationFromProduct(reservation);
		reservation.room = null;
		const product = this.products.find((obj) => obj.id == reservation.productId);
		this.reservationToCartPut(reservation, product);
	}

	private reservationToCartPut(reservation, product) {
		if (!product) {
			this.infobox.error(
				'Nie znaleziono produktu o ID: ' +
					reservation.productId +
					', rezerwacja: ' +
					reservation.businessCaseNumber,
			);
			return;
		}
		if (
			!this.reservationStyle('unassigned', reservation, {
				otherObjects: product.unassigned,
			})
		) {
			return;
		}
		if (reservation.row > product.unassignedRows) {
			product.unassignedRows = reservation.row;
		}
		product.unassigned.push(reservation);
	}

	resevationToNoname(reservation) {
		this.removeReservationFromProduct(reservation);
		reservation.room = null;
		this.resevationToNonamePut(reservation);
	}

	private resevationToNonamePut(reservation) {
		if (
			!this.reservationStyle('unassigned', reservation, {
				otherObjects: this.nonames,
			})
		) {
			return;
		}
		if (reservation.row > this.nonamesRows) {
			this.nonamesRows = reservation.row;
		}
		this.nonames.push(reservation);
	}

	private removeReservationFrom(reservation) {
		if (this.removeReservationFromNoname(reservation)) {
			return true;
		}
		if (this.removeReservationFromUnassigned(reservation)) {
			return true;
		}
		if (this.removeReservationFromProduct(reservation)) {
			return true;
		}
	}

	private removeReservationFromNoname(reservation): boolean {
		const index = this.nonames.findIndex((obj) => obj.id == reservation.id);
		if (index != -1) {
			this.nonames.splice(index, 1);
			return true;
		}
		return false;
	}

	private removeReservationFromUnassigned(reservation): boolean {
		const array = this.products.find((product) => product.unassigned.some((obj) => obj.id == reservation.id));
		if (!array) {
			return false;
		}
		const index = array.unassigned.findIndex((obj) => obj.id == reservation.id);
		if (index == -1) {
			return false;
		}
		array.unassigned.splice(index, 1);
		return true;
	}

	private removeReservationFromProduct(reservation): boolean {
		const array = this.products.find((product) => product.reservations.some((obj) => obj.id == reservation.id));
		if (!array) {
			return false;
		}
		const index = array.reservations.findIndex((obj) => obj.id == reservation.id);
		if (index == -1) {
			return false;
		}
		array.reservations.splice(index, 1);
		return true;
	}
}
